/**
 * @fileoverview The Agent Console digest table (`GenerativeAiAgentsApiDataTable`)
 * is shared by the Canvas, Data, and QA routes, but each category is gated
 * independently - one flipper cannot stand in for all three, because the
 * categories roll out on their own schedules. Both the digest scene (to decide
 * whether to enable `BrazeOperatorProvider`) and its registered page context
 * (to know whether the Create agent menu's tool is actually registered) need
 * the same answer, so the mapping and the predicate live here rather than in
 * either consumer.
 * @owner @Appboy/intelligence
 */

import type { FeatureName } from "@lib/featureFlipper";

import { AGENT_CATEGORIES } from "./agentCategory";
import type { AgentCategory } from "./agentCategory";

/**
 * The Operator scene-root feature flipper gating each Agent Console digest
 * category, or `null` once that category is fully rolled out and no longer
 * gated. Canvas reached 100% enablement on every cluster, so its flipper was
 * obsoleted and its entry is now `null`; Data and QA are still rolling out and
 * keep their own flippers.
 */
export const agentConsoleOperatorFeatureByCategory: Record<AgentCategory, FeatureName | null> = {
  [AGENT_CATEGORIES.CANVAS]: null,
  [AGENT_CATEGORIES.DATA]: "sage_ai_agentic_scene_root_agent_console_data_agents",
  [AGENT_CATEGORIES.QA]: "sage_ai_agentic_scene_root_agent_console_qa_agents",
};

/**
 * Whether Operator should be enabled for an Agent Console digest category. A
 * category with no flipper left is unconditionally enabled; otherwise its
 * flipper decides. Callers pass their own `featureOn` (from `useFeatureOn`) so
 * this stays a plain function rather than a hook.
 */
export const isAgentConsoleOperatorEnabled = (
  category: AgentCategory,
  featureOn: (feature: FeatureName) => boolean
): boolean => {
  const feature = agentConsoleOperatorFeatureByCategory[category];
  return feature === null || featureOn(feature);
};
